import { Bell, HelpCircle, Lock, LogOut, MoreVertical, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import TenantBrandMark from '../shared/TenantBrandMark';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/**
 * One-row shared masthead for applicants, onboarding employees and active staff.
 * Home already handles greeting and next actions. The masthead only answers:
 * which organisation am I in, which portal, and where are the utilities?
 */
export default function DashboardHeader({
  orgName,
  logoUrl,
  workerName,
  employeeCode,
  unreadCount = 0,
  hasPassword = false,
  onHelp,
  notificationsOpen = false,
  onRefresh,
  onToggleNotifications,
  onPassword,
  onLogout,
  mode = 'employee',
  portalLabel,
}) {
  const firstName = String(workerName || '').trim().split(/\s+/)[0] || 'there';
  const isApplicant = mode === 'applicant';
  const roleLabel = portalLabel || (isApplicant ? 'Applicant portal' : 'Workforce portal');

  return (
    <header
      className="relative z-20 border-b border-[#E4E8EB] bg-white shadow-[0_1px_0_rgba(15,92,94,0.06)]"
      aria-label={roleLabel}
      data-testid="worker-compact-masthead"
    >
      <div className="mx-auto flex min-h-[68px] max-w-4xl items-center gap-2 px-3 py-2 sm:min-h-[76px] sm:gap-4 sm:px-4">
        <div className="flex min-w-0 flex-1 items-center gap-2.5" data-testid="dashboard-tenant-identity">
          <TenantBrandMark orgName={orgName} logoUrl={logoUrl} />
          <div className="min-w-0 flex-1">
            <p
              className="truncate font-heading text-[13px] font-bold leading-tight text-[#17324D] sm:text-base"
              title={orgName}
              data-testid="dashboard-org-name"
            >
              {orgName || 'Your care provider'}
            </p>
            <p className="mt-0.5 truncate text-[11px] font-medium text-primary sm:text-xs">
              {roleLabel}
              <span className="sr-only">{`, signed in as ${workerName || firstName}`}</span>
            </p>
          </div>
        </div>

        <nav className="flex shrink-0 items-center gap-0.5 sm:gap-1" aria-label="Account and help">
          {onHelp ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={onHelp}
              aria-label="Get help"
              title="Help"
              className="min-h-10 min-w-10 rounded-xl"
            >
              <HelpCircle className="h-5 w-5" />
            </Button>
          ) : null}
          {onToggleNotifications ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleNotifications}
              aria-label="Notifications"
              aria-expanded={notificationsOpen}
              className="relative min-h-10 min-w-10 rounded-xl"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              ) : null}
            </Button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Account menu" title="Account" className="min-h-10 min-w-10 rounded-xl">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <div className="border-b border-slate-100 px-3 py-2">
                <p className="text-sm font-semibold text-slate-900">Hi, {firstName}</p>
                <p className="truncate text-xs text-slate-500">{roleLabel}</p>
                {employeeCode ? <p className="text-xs text-slate-500">Reference: {employeeCode}</p> : null}
              </div>
              {onRefresh ? (
                <DropdownMenuItem onClick={onRefresh}>
                  <RefreshCw className="mr-2 h-4 w-4" /> Refresh dashboard
                </DropdownMenuItem>
              ) : null}
              {onPassword ? (
                <DropdownMenuItem onClick={onPassword}>
                  <Lock className="mr-2 h-4 w-4" />
                  {hasPassword ? 'Change password' : 'Set password'}
                </DropdownMenuItem>
              ) : null}
              {onLogout ? (
                <DropdownMenuItem onClick={onLogout}>
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
    </header>
  );
}
